/**
 * 「改了要重新编排才生效」判据(2026-08-06 编排页重排,拍板 D5)。
 *
 * ═══ 治的是什么病 ═══════════════════════════════════════════════════════════════
 * 编排出分镜之后,brief 已被服务端快照 —— 此刻再改风格 / 拍法 / 比例,**成片不会变**。
 * 重排前的表现是:那些面板还在原位、作业结束后又变可点,用户改了没任何反应。
 * 这是一条**静默死通道**(重排诊断里的 P3),比报错更糟:报错至少说了话。
 *
 * ═══ 判据为什么钉在 ComposeRequest 字段集上 ═════════════════════════════════════
 * 只有一个不会漂移的口径:**ai 侧 `ComposeRequest` 真的会消费它吗**
 * (`gscq-ai/app/modules/video/api/routes.py::ComposeRequest`)。
 *   - 会消费 → 改了就是 stale,必须重编排;
 *   - 不消费(音色 / 语速 / 字幕大小 / BGM / 分辨率 / 模型 / 生成模式 / 负面词 / 任务名)
 *     → 那些是**出片期**参数,编排完再调正是它们该待的地方,改了不该报 stale。
 * 换任何别的口径(比如"备料台里的都算")都会误报,而误报几次之后用户就学会无视琥珀条,
 * 提示等于作废 —— 这跟漏报一样是失败。
 *
 * ★★ `ComposeRequest` 加字段时必须同步这份白名单,否则新字段改了不报 stale =
 *    P3 原样复发。`composeDirty.test.ts` 逐条锁住这张表,加字段会让测试红。
 */
import type { FormState } from "./videoCreateTypes";

/**
 * 进 `ComposeRequest` 的表单字段(逐条对着 ai 侧字段写,注释标出对应关系)。
 * `motionAssets` 不在此列 —— 它由 backend 从资产表带出,不是表单字段。
 */
export const COMPOSE_INPUT_KEYS = [
  "userInput", // → userInput
  "briefStyle", // → style
  "styleCode", // 与 briefStyle 同时被风格画廊写入;并列记以防只改其一
  "briefPurpose", // → purpose
  "briefDuration", // → durationTier
  "briefTone", // → tone(UI 已撤,字段仍在契约里)
  "briefBrand", // → brandName
  "ratio", // → ratio
  "storyMode", // → storyMode
  "entities", // → entities
  "productRefKey", // 素材:随 entities 一起决定产品 hero 镜
  "characterId", // 选人:逐字进 userInput(withCharacterSheet)+ entities
  "characterAppearance",
  // 0914 SEEDANCE 真人出镜 P0(顺手补齐,审查项③):选人时快照,与 characterId 三元组
  // 同生共死(applyCharacterPick/clearCharacterPick 同批写)——不与 characterId 分开跟踪
  // 的话,这条"哪个人物、他的认证/素材状态是什么"就可能悄悄跟丢,数字口径上仍算 dirty
  // 判据的一部分(即便不是逐字进 ai ComposeRequest 的字段,也随人物这一整块一起进出)。
  "characterVerificationStatus",
  "characterVerificationAssets",
  "protagonistAnchorKey",
  "explosiveFormulaPayload", // → formulaPayload
  "explosiveFidelityMode", // → fidelityMode
  "pickedFormulaKey", // → formulaKey
  // W4(2026-09-13):「换进去」钉的选料 —— 这是**输入**(下一次编排按它选料),不是
  // selectedFacts/unusedFacts 那类编排**结果**;结果字段不进这张表,见文件头注 ★★。
  "pinnedFactIds", // → pinnedFactIds
] as const satisfies readonly (keyof FormState)[];

/**
 * 明确**不算** stale 的字段 —— 只为可读性与测试而存在(运行时不读它)。
 * 写出来是因为「哪些不算」跟「哪些算」一样容易在后续改动里被搞错。
 */
export const NON_COMPOSE_KEYS = [
  "voiceTone",
  "voiceSpeechRate",
  "voiceVolume",
  "voiceEmotion",
  "subtitleFontScale",
  "subtitlePosition",
  "fontStyle",
  "transition",
  "bgmId",
  "customBgmObjectKey",
  "resolution",
  "provider",
  "model",
  "useAutoModel",
  "generationMode",
  "generateAudio",
  "negativePrompt",
  "durationSec",
  "name",
  "previewFrames",
] as const satisfies readonly (keyof FormState)[];

/** 实体只取影响编排的三元组:整对象带着 confidence/name 等噪音,抖一下就假 stale。 */
function projectEntities(entities: FormState["entities"]): string {
  if (!entities || entities.length === 0) return "";
  return entities.map((e) => `${e.role}|${e.key ?? ""}|${e.productId ?? ""}`).join(";");
}

/**
 * 编排输入指纹。**按 `COMPOSE_INPUT_KEYS` 的固定顺序**取值序列化 ——
 * 直接 `JSON.stringify(form)` 会把键序、以及一堆非编排字段一并卷进来,那样任何改动都 stale。
 */
export function composeInputDigest(form: FormState): string {
  const parts = COMPOSE_INPUT_KEYS.map((k) => {
    if (k === "entities") return projectEntities(form.entities);
    const v = form[k];
    if (v == null) return "";
    return typeof v === "string" ? v.trim() : String(v);
  });
  return parts.join(""); // 单元分隔符:正常文案里不会出现,不会假撞
}

/**
 * 相对 `snapshot`(上次编排时的指纹)是否已 stale。
 * snapshot 为空 = 还没编排过 → 一律 false(没编排就谈不上"要重新编排")。
 */
export function isComposeStale(snapshot: string | null | undefined, form: FormState): boolean {
  if (!snapshot) return false;
  return composeInputDigest(form) !== snapshot;
}
